import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { extract } from "@app/core";
import { TranslateService } from "@ngx-translate/core";
import { FreshdeskService, UserSettingsQuery } from "@timeghost/web-api";
import { BehaviorSubject } from "rxjs";
import { distinctUntilChanged, filter, finalize, map } from "rxjs/operators";
export enum FeedbackType {
	GENERIC = "generic",
	BUG = "bug",
	FEEDBACK = "feedback",
	ACCOUNT_DELETION = "account-deletion",
}
export const FeedbackMinResponse = {
	[FeedbackType.GENERIC]: 10,
	[FeedbackType.BUG]: 20,
	[FeedbackType.FEEDBACK]: 10,
	[FeedbackType.ACCOUNT_DELETION]: 0,
};
export interface FeedbackDialogConfig {
	description?: string;
	descriptionMinLength: number;
	type?: FeedbackType;
}
@Component({
	selector: "app-feedback-dialog",
	templateUrl: "./feedback-dialog.component.html",
	styleUrls: ["./feedback-dialog.component.scss"],
})
export class FeedbackDialogComponent implements OnInit {
	private _isLoading = new BehaviorSubject<boolean>(false);
	readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
	get isLoading() {
		return this._isLoading.getValue();
	}
	set isLoading(val: boolean) {
		this._isLoading.next(val);
	}
	feedbackTypes: typeof FeedbackType = FeedbackType;
	feedbackTypesArr = Object.keys(FeedbackType).map((x) => ({ key: x, value: FeedbackType[x] }));
	group: UntypedFormGroup;
	constructor(
		private ref: MatDialogRef<FeedbackDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		private data: Partial<FeedbackDialogConfig>,
		private freshdeskService: FreshdeskService,
		private translateService: TranslateService,
		private appService: AppService,
		userSettingsQuery: UserSettingsQuery
	) {
		const userSettings = userSettingsQuery.getValue();
		const initialType = data?.type || FeedbackType.GENERIC;
		this.group = new UntypedFormGroup({
			name: new UntypedFormControl(userSettings.officeProfile?.displayName || "", [
				Validators.required,
				CustomValidators.minLength(3),
			]),
			description: new UntypedFormControl(data?.description || "", this.getDescriptionValidators(initialType)),
			email: new UntypedFormControl(userSettings.officeProfile?.mail || "", [
				Validators.required,
				CustomValidators.minLength(3),
				Validators.email,
			]),
			type: new UntypedFormControl(initialType, [Validators.required]),
			newsletter_subscription: new UntypedFormControl(false),
			subject: new UntypedFormControl("", [Validators.required, CustomValidators.minLength(3)]),
		});
	}

	private getDescriptionValidators(type: FeedbackType) {
		if (type === FeedbackType.ACCOUNT_DELETION) {
			return [];
		}
		const minLength =
			this.data?.descriptionMinLength || FeedbackMinResponse[type] || FeedbackMinResponse[FeedbackType.GENERIC];
		return [Validators.required, CustomValidators.minLength(minLength)];
	}

	ngOnInit() {
		this.ref.addPanelClass(["mat-dialog-relative", "mat-dialog-vanilla"]);
		this.ref.updateSize("460px");
		this.group.valueChanges
			.pipe(
				map((x) => x?.type),
				filter((x) => x !== undefined),
				distinctUntilChanged()
			)
			.subscribe((x: FeedbackType) => {
				this.group.controls.description.setValidators(this.getDescriptionValidators(x));
				this.group.controls.description.updateValueAndValidity();
			});
	}
	submit() {
		if (this.group.invalid) {
			return;
		}
		this.isLoading = true;
		return this.freshdeskService
			.add({
				...this.group.value,
			})
			.pipe(
				finalize(() => {
					this.isLoading = false;
				})
			)
			.subscribe(
				(x) => {
					this.ref.close(x),
						this.appService.notifier.show({
							message: this.translateService.instant(extract("utils.ticket-created")),
							type: "success",
						});
				},
				(err) => {
					if (err?.errors) {
						this.appService.notifier.show({
							message: this.translateService.instant("errors.server"),
							type: "error",
						});
					}
				}
			);
	}
}
